import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Grid3X3,
  Home,
  RefreshCw,
  Loader2,
  Info,
  Save,
  Package,
  Plus
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Category Creation
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: "", description: "", image_url: "" });
  const [creating, setCreating] = useState(false);

  // Global category settings
  const [enabledCategories, setEnabledCategories] = useState([]);
  const [homepageCategories, setHomepageCategories] = useState([]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [catsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/categories`, getAuthHeaders()),
        axios.get(`${API}/admin/platform-settings`, getAuthHeaders())
      ]);

      setCategories(catsRes.data || []);
      setEnabledCategories(settingsRes.data?.enabled_categories || []);
      setHomepageCategories(settingsRes.data?.homepage_categories || []);
    } catch (error) {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await axios.post(`${API}/admin/categories`, categoryForm, getAuthHeaders());
      toast.success("Category created successfully");
      setCategoryDialogOpen(false);
      setCategoryForm({ name: "", description: "", image_url: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create category");
    } finally {
      setCreating(false);
    }
  };

  const toggleEnabledCategory = (categoryId) => {
    setEnabledCategories(prev => {
      const isCurrentlyEnabled = prev.includes(categoryId);
      if (isCurrentlyEnabled) {
        // Also remove from homepage when disabling
        setHomepageCategories(hp => hp.filter(id => id !== categoryId));
        return prev.filter(id => id !== categoryId);
      }
      return [...prev, categoryId];
    });
  };

  const toggleHomepageCategory = (categoryId) => {
    if (!enabledCategories.includes(categoryId)) {
      toast.error("Enable this category first before adding to homepage");
      return;
    }

    setHomepageCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      }
      return [...prev, categoryId];
    });
  };

  const selectAllCategories = () => {
    const allIds = categories.map(c => c.category_id);
    setEnabledCategories(allIds);
  };

  const deselectAllCategories = () => {
    setEnabledCategories([]);
    setHomepageCategories([]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/platform-settings`, {
        enabled_categories: enabledCategories,
        homepage_categories: homepageCategories
      }, getAuthHeaders());
      toast.success("Category settings saved!");
    } catch (error) {
      console.error("Save error:", error.response?.data || error.message);
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-categories">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Category Management
          </h1>
          <p className="text-slate-500">Control which categories appear on all reseller stores</p>
        </div>
        <div className="flex gap-3">
          <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 gap-2">
                <Plus className="w-4 h-4" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Category</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateCategory} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="cat_name">Category Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="cat_name"
                    required
                    value={categoryForm.name}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cat_desc">Description</Label>
                  <Textarea
                    id="cat_desc"
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                    rows={3}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cat_img">Image URL</Label>
                  <Input
                    id="cat_img"
                    value={categoryForm.image_url}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
                <Button type="submit" disabled={creating} className="w-full bg-indigo-600 hover:bg-indigo-700">
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Category
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700 gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-800">How this works</p>
          <p className="text-sm text-blue-700 mt-1">
            <strong>Available:</strong> Categories that resellers can see and sell products from.<br />
            <strong>Homepage:</strong> Categories featured on every reseller store&apos;s homepage.
          </p>
        </div>
      </div>

      {/* Bulk Actions & Summary */}
      <div className="flex flex-wrap items-center gap-3 bg-white p-4 rounded-xl border">
        <Button variant="outline" size="sm" onClick={selectAllCategories}>
          Select All Available
        </Button>
        <Button variant="outline" size="sm" onClick={deselectAllCategories}>
          Deselect All
        </Button>
        <div className="flex-1" />
        <Badge variant="secondary" className="text-sm">
          {enabledCategories.length} of {categories.length} available
        </Badge>
        <Badge variant="outline" className="text-sm">
          {homepageCategories.length} on homepage
        </Badge>
      </div>

      {/* Categories List */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        {categories.length === 0 ? (
          <div className="text-center py-16">
            <Grid3X3 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-600 mb-2">No categories found</p>
            <p className="text-sm text-slate-400 mb-6">Create a category to get started.</p>
            <Button onClick={() => setCategoryDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>
        ) : (
          <>
            {/* Header Row */}
            <div className="grid grid-cols-12 gap-4 px-6 py-3 bg-slate-50 border-b text-sm font-medium text-slate-600">
              <div className="col-span-5">Category</div>
              <div className="col-span-2 text-center">Products</div>
              <div className="col-span-2 text-center">Available</div>
              <div className="col-span-3 text-center">Homepage</div>
            </div>

            {/* Category Rows */}
            <div className="divide-y">
              {categories.map((category) => {
                const isEnabled = enabledCategories.includes(category.category_id);
                const isHomepage = homepageCategories.includes(category.category_id);

                return (
                  <div
                    key={category.category_id}
                    className={`grid grid-cols-12 gap-4 px-6 py-4 transition-colors ${isEnabled ? 'bg-white' : 'bg-slate-50/50'
                      }`}
                    data-testid={`category-row-${category.category_id}`}
                  >
                    {/* Category Info */}
                    <div className="col-span-5 flex items-center gap-4">
                      {category.image_url ? (
                        <img
                          src={category.image_url}
                          alt={category.name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-slate-200 rounded-lg flex items-center justify-center">
                          <Package className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <p className={`font-medium ${isEnabled ? 'text-slate-900' : 'text-slate-500'}`}>
                          {category.name}
                        </p>
                        {category.description && (
                          <p className="text-xs text-slate-400 truncate max-w-[250px]">
                            {category.description}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Product Count */}
                    <div className="col-span-2 flex items-center justify-center">
                      <span className={`text-sm font-medium ${isEnabled ? 'text-slate-700' : 'text-slate-400'}`}>
                        {category.product_count || 0}
                      </span>
                    </div>

                    {/* Available Toggle */}
                    <div className="col-span-2 flex items-center justify-center">
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={() => toggleEnabledCategory(category.category_id)}
                        data-testid={`category-available-${category.category_id}`}
                      />
                    </div>

                    {/* Homepage Toggle */}
                    <div className="col-span-3 flex items-center justify-center gap-2">
                      <Switch
                        checked={isHomepage}
                        onCheckedChange={() => toggleHomepageCategory(category.category_id)}
                        disabled={!isEnabled}
                        data-testid={`category-homepage-${category.category_id}`}
                      />
                      {isHomepage && (
                        <Home className="w-4 h-4 text-amber-600" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Footer Save Button */}
      {categories.length > 0 && (
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-amber-600 hover:bg-amber-700 gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
}
